import type { Objective1 } from '../types/Objective1';
import type { MilestoneMap1 } from '../types/MilestoneMap1';

/**
 * LEGACY-mode (order-based) hierarchy helpers, moved unchanged from
 * mm-web utils/objective-utils.ts. See ./README.md.
 */

/**
 * Gets child objectives for a specific major objective based on their order in the milestone map.
 *
 * This function infers parent-child relationships based on the ORDER of objectives in
 * milestoneMap.objectiveUuids rather than using objective.parentIds or objective.childIds,
 * which are unreliable due to data integrity issues.
 *
 * The logic assumes that objectives appearing after a major objective but before the next
 * major objective are children of that major objective.
 *
 * @param milestoneMap - The milestone map containing objective ordering information
 * @param objectives - Array of all objectives for this map
 * @param majorObjectiveUuid - UUID of the major objective to find children for
 * @returns Array of child objectives for the specified major objective
 *
 * TODO: This logic is suboptimal and should be replaced with a more direct approach
 * using utility functions once the data integrity issues around parent-child
 * relationships in objective documents are corrected.
 */
export function getChildObjectivesForParentObjectiveByOrder(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  majorObjectiveUuid: string,
): Objective1[] {
  const majorObjectiveUuids = milestoneMap.parentObjectiveUuids || [];
  const orderedObjectiveUuids = milestoneMap.objectiveUuids || [];

  // Create objective lookup map
  const objectiveMap = new Map<string, Objective1>();
  objectives.forEach((obj) => objectiveMap.set(obj.uuid, obj));

  // Get objectives in the order defined by milestoneMap.objectiveUuids
  const orderedObjectives = orderedObjectiveUuids
    .map((uuid) => objectiveMap.get(uuid))
    .filter((obj): obj is Objective1 => obj !== undefined);

  // Find the index of this major objective in the ordered list
  const majorObjIndex = orderedObjectives.findIndex((obj) => obj.uuid === majorObjectiveUuid);

  if (majorObjIndex === -1) {
    return []; // Major objective not found
  }

  // Find all major objective indices to determine children range
  const majorObjIndices = orderedObjectives
    .map((obj, index) => (majorObjectiveUuids.includes(obj.uuid) ? index : -1))
    .filter((index) => index !== -1);

  const currentMajorIndex = majorObjIndices.indexOf(majorObjIndex);
  const nextMajorIndex =
    currentMajorIndex + 1 < majorObjIndices.length
      ? majorObjIndices[currentMajorIndex + 1]
      : orderedObjectives.length;

  // Return child objectives (those that appear after this major objective but before the next)
  return orderedObjectives.slice(majorObjIndex + 1, nextMajorIndex);
}

/**
 * Calculates the correct parent/child relationships for all objectives in a milestone map
 * based on the current order and parent designations, returning the updates needed for each objective.
 *
 * This function analyzes the milestone map structure and determines:
 * - Which objectives should have parentIds updated based on their position relative to parent objectives
 * - Which parent objectives should have childIds updated based on the children that follow them
 * - Preserves relationships from other milestone maps by only modifying relationships relevant to this map
 *
 * The function only includes an objective in the returned updates if its relationships have actually changed
 * (i.e., if the current parentIds/childIds differ from what they should be based on map ordering).
 *
 * @param milestoneMap - The milestone map containing objective ordering and parent designations
 * @param objectives - Array of all objectives for this map
 * @returns Object mapping objective UUIDs to the updates needed for their parentIds and childIds
 */
export function calculateObjectiveRelationshipUpdates(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
): Record<string, { parentIds?: string[]; childIds?: string[] }> {
  const updates: Record<string, { parentIds?: string[]; childIds?: string[] }> = {};
  const parentObjectiveUuids = milestoneMap.parentObjectiveUuids || [];
  const orderedObjectiveUuids = milestoneMap.objectiveUuids || [];

  // Create objective lookup map for fast access
  const objectiveMap = new Map<string, Objective1>();
  objectives.forEach((obj) => objectiveMap.set(obj.uuid, obj));

  // Process each objective to determine its relationships
  orderedObjectiveUuids.forEach((objectiveUuid, index) => {
    const objective = objectiveMap.get(objectiveUuid);
    if (!objective) return;

    const isParentObjective = parentObjectiveUuids.includes(objectiveUuid);

    if (isParentObjective) {
      // This is a parent objective - update its childIds while preserving cross-map relationships
      const childObjectives = getChildObjectivesForParentObjectiveByOrder(
        milestoneMap,
        objectives,
        objectiveUuid,
      );
      const childrenOnThisMap = childObjectives.map((child) => child.uuid);

      // Preserve existing childIds that are NOT on this milestone map (they're from other maps)
      const currentChildIds = objective.childIds || [];
      const childrenFromOtherMaps = currentChildIds.filter(
        (childId) => !orderedObjectiveUuids.includes(childId),
      );

      // Combine children from this map with children from other maps
      const newChildIds = [...childrenFromOtherMaps, ...childrenOnThisMap];

      // Only update if childIds have changed
      if (JSON.stringify([...currentChildIds].sort()) !== JSON.stringify([...newChildIds].sort())) {
        updates[objectiveUuid] = { ...updates[objectiveUuid], childIds: newChildIds };
      }
    } else {
      // This is a potential child objective - update its parentIds while preserving cross-map relationships
      const nearestParentIndex = findNearestParentIndex(
        orderedObjectiveUuids,
        parentObjectiveUuids,
        index,
      );

      const parentOnThisMap: string[] = [];
      if (nearestParentIndex !== -1) {
        const parentUuid = orderedObjectiveUuids[nearestParentIndex];
        parentOnThisMap.push(parentUuid);
      }

      // Preserve existing parentIds that are NOT parent objectives on this milestone map (they're from other maps)
      const currentParentIds = objective.parentIds || [];
      const parentsFromOtherMaps = currentParentIds.filter(
        (parentId) => !parentObjectiveUuids.includes(parentId),
      );

      // Combine parent from this map with parents from other maps
      const newParentIds = [...parentsFromOtherMaps, ...parentOnThisMap];

      // Only update if parentIds have changed
      if (
        JSON.stringify([...currentParentIds].sort()) !== JSON.stringify([...newParentIds].sort())
      ) {
        updates[objectiveUuid] = { ...updates[objectiveUuid], parentIds: newParentIds };
      }
    }
  });

  return updates;
}

/**
 * Helper function to find the nearest parent objective that appears before a given index in the ordered list.
 * This determines which parent objective should be the parent for a child objective based on the map ordering.
 */
function findNearestParentIndex(
  orderedObjectiveUuids: string[],
  parentObjectiveUuids: string[],
  childIndex: number,
): number {
  // Look backwards from the child's position to find the nearest parent
  for (let i = childIndex - 1; i >= 0; i--) {
    const objectiveUuid = orderedObjectiveUuids[i];
    if (parentObjectiveUuids.includes(objectiveUuid)) {
      return i;
    }
  }
  return -1; // No parent found
}

/**
 * Calculates the parent/child relationship updates needed when an objective is removed from a milestone map.
 * This function ensures that when an objective is removed, any stale parent/child relationships are cleaned up
 * while preserving relationships from other milestone maps.
 *
 * @param milestoneMap - The milestone map the objective is being removed from
 * @param removedObjectiveUuid - The UUID of the objective being removed
 * @param objectives - Array of all objectives for this map
 * @returns Object mapping objective UUIDs to the updates needed for their parentIds and childIds
 */
export function calculateObjectiveRemovalUpdates(
  milestoneMap: MilestoneMap1,
  removedObjectiveUuid: string,
  objectives: Objective1[],
): Record<string, { parentIds?: string[]; childIds?: string[] }> {
  const updates: Record<string, { parentIds?: string[]; childIds?: string[] }> = {};
  const parentObjectiveUuids = milestoneMap.parentObjectiveUuids || [];
  const orderedObjectiveUuids = milestoneMap.objectiveUuids || [];

  // Create objective lookup map for fast access
  const objectiveMap = new Map<string, Objective1>();
  objectives.forEach((obj) => objectiveMap.set(obj.uuid, obj));

  const removedObjective = objectiveMap.get(removedObjectiveUuid);
  if (!removedObjective) return updates;

  const isRemovedObjectiveParent = parentObjectiveUuids.includes(removedObjectiveUuid);

  if (isRemovedObjectiveParent) {
    // Scenario 1: A parent is being removed
    // 1. Remove this parent from its children's parentIds (only if the parent was on this map)
    // 2. Remove children from this parent's childIds (only children that were on this map)

    const childrenOnThisMap = getChildObjectivesForParentObjectiveByOrder(
      milestoneMap,
      objectives,
      removedObjectiveUuid,
    );

    // Update each child to remove this parent from their parentIds
    childrenOnThisMap.forEach((child) => {
      const currentParentIds = child.parentIds || [];
      const newParentIds = currentParentIds.filter((parentId) => parentId !== removedObjectiveUuid);

      if (
        JSON.stringify([...currentParentIds].sort()) !== JSON.stringify([...newParentIds].sort())
      ) {
        updates[child.uuid] = { ...updates[child.uuid], parentIds: newParentIds };
      }
    });

    // Update the parent to remove children that were on this map
    const currentChildIds = removedObjective.childIds || [];
    const childrenOnThisMapIds = childrenOnThisMap.map((child) => child.uuid);
    const newChildIds = currentChildIds.filter(
      (childId) => !childrenOnThisMapIds.includes(childId),
    );

    if (JSON.stringify([...currentChildIds].sort()) !== JSON.stringify([...newChildIds].sort())) {
      updates[removedObjectiveUuid] = { ...updates[removedObjectiveUuid], childIds: newChildIds };
    }
  } else {
    // Scenario 2: A child is being removed
    // 1. Remove this child from its parent's childIds (only if the parent was on this map)
    // 2. Remove parent from this child's parentIds (only parents that were on this map)

    const removedObjectiveIndex = orderedObjectiveUuids.indexOf(removedObjectiveUuid);
    if (removedObjectiveIndex !== -1) {
      const nearestParentIndex = findNearestParentIndex(
        orderedObjectiveUuids,
        parentObjectiveUuids,
        removedObjectiveIndex,
      );

      if (nearestParentIndex !== -1) {
        const parentUuid = orderedObjectiveUuids[nearestParentIndex];
        const parentObjective = objectiveMap.get(parentUuid);

        if (parentObjective) {
          // Remove this child from the parent's childIds
          const currentChildIds = parentObjective.childIds || [];
          const newChildIds = currentChildIds.filter((childId) => childId !== removedObjectiveUuid);

          if (
            JSON.stringify([...currentChildIds].sort()) !== JSON.stringify([...newChildIds].sort())
          ) {
            updates[parentUuid] = { ...updates[parentUuid], childIds: newChildIds };
          }
        }
      }
    }

    // Remove parents that were on this map from the child's parentIds
    const currentParentIds = removedObjective.parentIds || [];
    const parentsOnThisMap = currentParentIds.filter((parentId) =>
      parentObjectiveUuids.includes(parentId),
    );
    const newParentIds = currentParentIds.filter(
      (parentId) => !parentsOnThisMap.includes(parentId),
    );

    if (JSON.stringify([...currentParentIds].sort()) !== JSON.stringify([...newParentIds].sort())) {
      updates[removedObjectiveUuid] = { ...updates[removedObjectiveUuid], parentIds: newParentIds };
    }
  }

  return updates;
}

/**
 * Checks if there are any parent/child relationship discrepancies in a milestone map
 * by comparing current objective relationships with what they should be based on map ordering.
 *
 * @param milestoneMap - The milestone map to check
 * @param objectives - Array of all objectives for this map
 * @returns Object with discrepancy information: { hasDiscrepancies: boolean, updates: Record<string, updates> }
 */
export function checkForRelationshipDiscrepancies(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
): {
  hasDiscrepancies: boolean;
  updates: Record<string, { parentIds?: string[]; childIds?: string[] }>;
} {
  const updates = calculateObjectiveRelationshipUpdates(milestoneMap, objectives);
  const hasDiscrepancies = Object.keys(updates).length > 0;

  return { hasDiscrepancies, updates };
}
